/**
 * Locked per-engine gas, recharge, and notification defaults (frontend mirror).
 * Source of truth: IdxAutoBotNext/docs/bot-gas-recharge-tables.md
 */

import {
  isV4EcoEngine,
  isPairEngine,
  isMiniBuyEngine,
  isMiniBuySellEngine,
  isMiniBuySellXEngine,
  isV12TurboEngine,
  isV10Engine,
  isPortfolioComboEngine,
} from "./engines";

function bands(list) {
  return list.map(([threshold, level, color, iconGlow]) => [
    threshold,
    level,
    color,
    Boolean(iconGlow),
  ]);
}

const GAS_BANDS = {
  V4_ECO: bands([
    [10.0, 10, "green", false],
    [7.0, 9, "green", false],
    [5.0, 8, "green", false],
    [3.6, 7, "green", false],
    [3.0, 6, "green", false],
    [2.5, 5, "green", false],
    [2.0, 4, "orange", false],
    [1.5, 3, "orange", false],
    [1.0, 2, "red", true],
    [0.5, 1, "red", true],
    [0, 0, "red", true],
  ]),
  V4: bands([
    [10.0, 10, "green", false],
    [7.0, 9, "green", false],
    [5.0, 8, "green", false],
    [4.0, 7, "green", false],
    [3.0, 6, "green", false],
    [2.5, 5, "green", false],
    [2.0, 4, "orange", false],
    [1.5, 3, "orange", false],
    [1.0, 2, "red", true],
    [0.5, 1, "red", true],
    [0, 0, "red", true],
  ]),
  V6: bands([
    [18.0, 10, "green", false],
    [12.0, 9, "green", false],
    [9.0, 8, "green", false],
    [7.0, 7, "green", false],
    [6.0, 6, "green", false],
    [5.5, 5, "green", false],
    [4.0, 4, "orange", false],
    [3.0, 3, "orange", false],
    [2.0, 2, "red", true],
    [0.5, 1, "red", true],
    [0, 0, "red", true],
  ]),
  V8: bands([
    [27.0, 10, "green", false],
    [18.0, 9, "green", false],
    [14.0, 8, "green", false],
    [11.0, 7, "green", false],
    [9.0, 6, "green", false],
    [8.0, 5, "green", false],
    [6.0, 4, "orange", false],
    [4.5, 3, "orange", false],
    [3.0, 2, "red", true],
    [0.5, 1, "red", true],
    [0, 0, "red", true],
  ]),
  V12: bands([
    [162.0, 10, "green", false],
    [108.0, 9, "green", false],
    [84.0, 8, "green", false],
    [66.0, 7, "green", false],
    [54.0, 6, "green", false],
    [48.0, 5, "green", false],
    [36.0, 4, "orange", false],
    [27.0, 3, "orange", false],
    [18.0, 2, "red", true],
    [1.5, 1, "red", true],
    [0, 0, "red", true],
  ]),
  V10: bands([
    [54.0, 10, "green", false],
    [36.0, 9, "green", false],
    [28.0, 8, "green", false],
    [22.0, 7, "green", false],
    [18.0, 6, "green", false],
    [16.0, 5, "green", false],
    [12.0, 4, "orange", false],
    [9.0, 3, "orange", false],
    [6.0, 2, "red", true],
    [1.0, 1, "red", true],
    [0, 0, "red", true],
  ]),
  MINI_BUY: bands([
    [4.5, 10, "green", false],
    [3.2, 9, "green", false],
    [2.3, 8, "green", false],
    [1.7, 7, "green", false],
    [1.35, 6, "green", false],
    [1.15, 5, "green", false],
    [0.95, 4, "orange", false],
    [0.7, 3, "orange", false],
    [0.45, 2, "red", true],
    [0.25, 1, "red", true],
    [0, 0, "red", true],
  ]),
  PORTFOLIO_COMBO: bands([
    [32.0, 10, "green", false],
    [21.0, 9, "green", false],
    [16.0, 8, "green", false],
    [13.0, 7, "green", false],
    [10.5, 6, "green", false],
    [9.0, 5, "green", false],
    [7.0, 4, "orange", false],
    [5.5, 3, "orange", false],
    [3.5, 2, "red", true],
    [3.0, 1, "red", true],
    [0, 0, "red", true],
  ]),
  GLOBAL: bands([
    [6.5, 10, "green", false],
    [5.5, 9, "green", false],
    [4.5, 8, "green", false],
    [3.5, 7, "green", false],
    [2.5, 6, "green", false],
    [2.0, 5, "green", false],
    [1.5, 4, "orange", false],
    [0.8, 3, "orange", false],
    [0.5, 2, "red", true],
    [0.3, 1, "red", true],
    [0, 0, "red", true],
  ]),
};

const PROFILES = {
  V4_ECO: {
    firstRechargeMinSol: 3.6,
    subsequentRechargeMinSol: 2.0,
    stopDisplaySol: "0.5",
    lowGasBannerLevel: 5,
  },
  /** Pair: Eco gas bands; first recharge raised for 20% fee vs Eco 2.7%. */
  PAIR: {
    firstRechargeMinSol: 4.4,
    subsequentRechargeMinSol: 2.5,
    stopDisplaySol: "0.5",
    lowGasBannerLevel: 5,
    gasBandKey: "V4_ECO",
  },
  V4: {
    firstRechargeMinSol: 4.0,
    subsequentRechargeMinSol: 2.4,
    stopDisplaySol: "0.5",
    lowGasBannerLevel: 5,
  },
  V6: {
    firstRechargeMinSol: 8.0,
    subsequentRechargeMinSol: 4.0,
    stopDisplaySol: "0.5",
    lowGasBannerLevel: 3,
  },
  V8: {
    firstRechargeMinSol: 12.0,
    subsequentRechargeMinSol: 6.0,
    stopDisplaySol: "0.5",
    lowGasBannerLevel: 3,
  },
  V12: {
    firstRechargeMinSol: 72.0,
    subsequentRechargeMinSol: 36.0,
    stopDisplaySol: "1.5",
    lowGasBannerLevel: 3,
  },
  V10: {
    firstRechargeMinSol: 24.0,
    subsequentRechargeMinSol: 12.0,
    stopDisplaySol: "1",
    lowGasBannerLevel: 3,
  },
  MINI_BUY: {
    firstRechargeMinSol: 2.0,
    subsequentRechargeMinSol: 1.0,
    stopDisplaySol: "0.05",
    lowGasBannerLevel: 4,
  },
  MINI_BUY_SELL: {
    firstRechargeMinSol: 4.0,
    subsequentRechargeMinSol: 2.0,
    stopDisplaySol: "0.05",
    lowGasBannerLevel: 4,
    gasBandKey: "GLOBAL",
  },
  MINI_BUY_SELL_X: {
    firstRechargeMinSol: 4.0,
    subsequentRechargeMinSol: 2.0,
    stopDisplaySol: "0.05",
    lowGasBannerLevel: 4,
    gasBandKey: "GLOBAL",
  },
  V12_TURBO: {
    firstRechargeMinSol: 2.0,
    subsequentRechargeMinSol: 1.0,
    stopDisplaySol: "0.05",
    lowGasBannerLevel: 4,
    gasBandKey: "MINI_BUY",
  },
  PORTFOLIO_COMBO: {
    firstRechargeMinSol: 14.0,
    subsequentRechargeMinSol: 7.0,
    stopDisplaySol: "3",
    lowGasBannerLevel: 3,
  },
};

export function normalizeEngineGasKey(engine) {
  const e = String(engine ?? "").trim().toUpperCase();
  if (isPairEngine(engine) || e === "PAIR" || e === "PAIRBOT") return "PAIR";
  if (isV4EcoEngine(engine) || e === "V4ECO") return "V4_ECO";
  if (isMiniBuyEngine(engine)) return "MINI_BUY";
  if (isMiniBuySellXEngine(engine)) return "MINI_BUY_SELL_X";
  if (isMiniBuySellEngine(engine)) return "MINI_BUY_SELL";
  if (isV12TurboEngine(engine)) return "V12_TURBO";
  if (isPortfolioComboEngine(engine)) return "PORTFOLIO_COMBO";
  if (isV10Engine(engine)) return "V10";
  if (e === "V8") return "V8";
  if (e === "V12") return "V12";
  if (e === "V6") return "V6";
  if (e === "V4") return "V4";
  return null;
}

export function getEngineGasProfile(engine) {
  const key = normalizeEngineGasKey(engine);
  if (!key) return null;
  return { key, ...PROFILES[key] };
}

export function getGasBandsForEngine(engine) {
  const key = normalizeEngineGasKey(engine);
  if (!key) return GAS_BANDS.GLOBAL;
  const profile = PROFILES[key];
  const bandKey = profile.gasBandKey || key;
  return GAS_BANDS[bandKey] || GAS_BANDS.GLOBAL;
}

export function calculateGasFeesFromBalanceForEngine(engine, totalBalance) {
  const balance = Number(totalBalance) || 0;
  const thresholds = getGasBandsForEngine(engine);
  for (const [threshold, level, color, iconGlow] of thresholds) {
    if (balance > threshold) {
      return { level, color, iconGlow };
    }
  }
  return { level: 0, color: "red", iconGlow: true };
}

export function getFirstRechargeMinSolDefault(engine) {
  const p = getEngineGasProfile(engine);
  return p ? p.firstRechargeMinSol : null;
}

export function getSubsequentRechargeMinSolDefault(engine) {
  const p = getEngineGasProfile(engine);
  return p ? p.subsequentRechargeMinSol : null;
}

export function getStopDisplaySol(engine) {
  const p = getEngineGasProfile(engine);
  return p ? p.stopDisplaySol : "0.5";
}

export function getStartGateMinSolEquivalent(engine, isFirstRechargeDone) {
  const p = getEngineGasProfile(engine);
  if (!p) return null;
  return isFirstRechargeDone
    ? p.subsequentRechargeMinSol
    : p.firstRechargeMinSol;
}

export function getLowGasBannerLevel(engine) {
  const p = getEngineGasProfile(engine);
  return p ? p.lowGasBannerLevel : 5;
}

/** True when per-engine gas/recharge tables apply (all production engines). */
export function isLockedGasEngine(engine) {
  return normalizeEngineGasKey(engine) != null;
}

/** Volume engine SOL tab copy (no SOL number in headline). */
export function getClassicVolumeGasTipsCopy() {
  return {
    headline: "Top up to keep gas green.",
    subline: "Orange/red: add SOL soon. More SOL if you trade heavy.",
  };
}

function envMinSol(primary, fallback, defaultValue) {
  if (typeof process === "undefined") return defaultValue;
  const raw = [primary, fallback].find(
    (v) => v != null && String(v).trim() !== "",
  );
  if (raw == null) return defaultValue;
  const n = Number(raw);
  return Number.isFinite(n) ? n : defaultValue;
}

/** First recharge floor with optional env override (dev/staging, non-locked engines only). */
export function resolveFirstRechargeMinSol(engine, envKeys = {}) {
  const def = getFirstRechargeMinSolDefault(engine);
  if (def == null) return null;
  if (isLockedGasEngine(engine)) return def;
  return envMinSol(envKeys.public, envKeys.server, def);
}

/** Subsequent recharge floor with optional env override (dev/staging, non-locked engines only). */
export function resolveSubsequentRechargeMinSol(engine, envKeys = {}) {
  const def = getSubsequentRechargeMinSolDefault(engine);
  if (def == null) return null;
  if (isLockedGasEngine(engine)) return def;
  return envMinSol(envKeys.public, envKeys.server, def);
}
